import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Trophy, User, Wallet, Target, HelpCircle, 
  LogOut, Menu, X
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useWallet } from '../context/WalletContext';
import { Button } from './ui/button';
import NotificationPopup from './NotificationPopup';

const Navbar = () => {
  const { user, logout } = useUser();
  
  // Safe wallet access with error handling
  let wallet = null;
  try {
    const walletContext = useWallet();
    wallet = walletContext?.wallet;
  } catch (error) {
    // WalletContext might not be available, use fallback
    console.log('Wallet context not available:', error.message);
  }
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const triggerAuth = () => {
    window.dispatchEvent(new Event('auth-trigger'));
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Trophy },
    { name: 'My Contests', href: '/my-contests', icon: Target },
    { name: 'Wallet', href: '/wallet', icon: Wallet },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Help', href: '/help', icon: HelpCircle }
  ];

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center space-x-2 flex-shrink-0">
            <div className="bg-red-600 p-2 rounded-lg">
              <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-bold text-gray-900">WinQuizz</span>
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <div className="hidden md:flex items-center space-x-6">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-red-100 text-red-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Right Side */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {user ? (
              <>
                {/* Desktop User Menu with Notification */}
                <div className="hidden md:flex items-center space-x-3">
                  <div className="hidden lg:block text-right">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-32">{user.full_name}</p>
                    <p className="text-xs text-gray-600 truncate max-w-32">@{user.username}</p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Desktop Notification - Integrated in Navbar */}
                    <NotificationPopup />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogout}
                      className="hidden sm:flex items-center space-x-1"
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="hidden lg:inline">Logout</span>
                    </Button>
                  </div>
                </div>

                {/* Mobile Notification & User Profile - SWAPPED ORDER */}
                <div className="md:hidden flex items-center space-x-3">
                  {/* Mobile Notification - NOW LEFT */}
                  <div className="relative">
                    <NotificationPopup />
                  </div>
                  
                  {/* Mobile User Profile - NOW RIGHT */}
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                  >
                    <User className="h-5 w-5 text-red-600" />
                  </button>
                </div>
              </>
            ) : (
              <Button onClick={triggerAuth} className="bg-red-600 hover:bg-red-700 text-sm sm:text-base">
                <span className="hidden sm:inline">Login / Sign Up</span>
                <span className="sm:hidden">Login</span>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile User Panel */}
        {user && isMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMenuOpen(false)}>
            <div className="fixed top-0 right-0 w-80 max-w-full h-full bg-white shadow-lg" onClick={(e) => e.stopPropagation()}>
              {/* User Header */}
              <div className="p-6 border-b border-gray-200 bg-red-50">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-semibold text-gray-900 truncate">{user.full_name}</p>
                    <p className="text-sm text-gray-600 truncate">@{user.username}</p>
                  </div>
                  <button 
                    onClick={() => setIsMenuOpen(false)}
                    className="p-1 rounded-md text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Navigation Menu */}
              <div className="flex-1 overflow-y-auto py-4">
                <div className="space-y-1 px-4">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          isActive
                            ? 'bg-red-100 text-red-600'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
                
                {/* Wallet Info */}
                <div className="mx-4 mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Wallet Balance</span>
                    <span className="text-lg font-bold text-gray-900">
                      ₹{wallet?.total_amount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Bonus: ₹{wallet?.bonus_amount?.toFixed(2) || '0.00'}
                  </div>
                </div>

                {/* Logout */}
                <div className="mx-4 mt-4">
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 w-full text-left"
                  >
                    <LogOut className="h-5 w-5 flex-shrink-0" />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;